/**
 * English copy. This file is the fallback for every other language, so a key
 * must exist here before it is used anywhere.
 *
 * Wording rules, which apply to every string added later:
 *   - describe what the data shows, never what will happen;
 *   - never state or imply a performance result, a forecast, or that a client
 *     or a product is suitable or regulatory-approved;
 *   - say "review", "verify", "record" — the adviser decides, the platform
 *     puts the evidence and its limits in front of them.
 */
const en = {
    'common.language': 'Language',
    'common.language.en': 'English',
    'common.language.es': 'Español',
    'common.syntheticBadge': 'Synthetic data',
    'common.notAvailable': 'not available',
    'common.missing': 'missing',

    // Shared chrome. The demo and the registration are the two pages a
    // Spanish visitor reaches first, and both carry this navigation bar and
    // this footer; leaving them in English undoes the translation above them.
    'nav.plans': 'Features & Plans',
    'nav.login': 'Login',
    'footer.rights': '© {year} FinanSee. All rights reserved.',
    'footer.support': 'Support:',
    'footer.legal.privacy': 'Privacy Policy',
    'footer.legal.terms': 'Terms of Use',
    'footer.legal.cookies': 'Cookies Policy',
    'footer.legal.disclaimer': 'Legal Disclaimer',
    'footer.dpa': 'DPA template on request ->',

    // --- Public demo -------------------------------------------------------
    'demo.meta.title': 'Public demo — review a client book | FinanSee',
    'demo.meta.description': 'Walk the client review path on a synthetic book of a Spanish independent advisory firm: review list, client card and review report. No registration, no client data of your own.',

    'demo.hero.badge': 'Public demo · synthetic data',
    'demo.hero.title': 'See how a client book is reviewed, without registering.',
    'demo.hero.subtitle': 'This is the synthetic book of an independent advisory firm: {clients} clients, one adviser. Walk the three screens the work actually happens on — the review list, the client card and the review report.',
    'demo.hero.point.noSignup': 'No registration and no email',
    'demo.hero.point.noData': 'None of your client data',
    'demo.hero.point.oneMinute': 'Under a minute end to end',
    'demo.hero.cta': 'Open the review list',

    'demo.disclosure.title': 'Everything on this page is synthetic',
    'demo.disclosure.body': 'The clients, names, amounts and dates below were written for this demo. Nothing comes from a real client, firm or market series, and no number here is a performance result. With your own data the same screens show your book instead.',

    'demo.book.title': 'The book this firm uploaded',
    'demo.book.file': 'File',
    'demo.book.rows': 'Clients in the extract',
    'demo.book.assets': 'Assets under advice',
    'demo.book.median': 'Median client',
    'demo.book.coverage': 'Evidence coverage',
    'demo.book.coverageHint': 'Share of the fields the ranking needs that this extract fills.',
    'demo.book.observed': 'Data observed on',
    'demo.book.imported': 'Imported on',
    'demo.book.undated': 'Clients with no source date',
    'demo.book.undatedHint': 'Listed as undated, never assumed to be current.',
    'demo.book.notRankable': 'Clients that cannot be ranked',
    'demo.book.notRankableHint': 'Evidence too thin or too old to compare with the rest of the book.',
    'demo.book.firm': 'Independent advisory firm, {city} · {advisers} adviser, {analysts} analyst',

    'demo.steps.one': 'Review list',
    'demo.steps.two': 'Client card',
    'demo.steps.three': 'Review report',

    'demo.queue.title': '1. The clients to review',
    'demo.queue.subtitle': 'Review order inside this book. Not a risk measure and not a probability.',
    'demo.queue.showing': 'Showing {shown} examples from a synthetic book of {total} clients, including cases to verify.',
    'demo.queue.col.client': 'Client',
    'demo.queue.col.order': 'Order',
    'demo.queue.col.reason': 'Why it is on the list',
    'demo.queue.col.evidence': 'Evidence',
    'demo.queue.col.observed': 'Data observed',
    'demo.queue.col.adviser': 'Adviser',
    'demo.queue.open': 'Open card',
    'demo.queue.openAria': 'Open the card of {name}',
    'demo.queue.undated': 'No date in the extract',
    'demo.queue.coverage': '{value}% of fields',

    'demo.guide.title': 'How to read this list',
    'demo.guide.order': 'The scores in this demo are illustrative synthetic inputs. Urgency labels use the engine\'s rules on the full book; this is not a full customer-scoring calculation or a risk measure.',
    'demo.guide.labels': 'The limits use all {total} clients in the book, not only the {ranked} with usable evidence. This example has {high} High and {medium} Medium priorities. A score below {minimum} cannot enter either group.',
    'demo.guide.low': 'The other actionable clients remain in regular review. The {notRankable} clients without usable evidence are kept separate for verification, without an actionable score.',
    'demo.guide.reference': 'Fixed synthetic example evaluated as of {date}, rather than today.',
    'demo.guide.missing': 'Missing data stays missing. It is never counted as a zero and never turned into a signal.',
    'demo.guide.change': 'Change over time needs two dated extracts. This is the first one, so no trend is shown yet.',

    'demo.urgency.high': 'High',
    'demo.urgency.medium': 'Medium',
    'demo.urgency.low': 'Low',
    'demo.urgency.verify': 'Verify',
    'demo.priority.unscored': 'Not ranked',

    'demo.client.title': '2. Client card',
    'demo.client.subtitle': 'What the extract contains about {name}, and what it does not.',
    'demo.client.facts': 'From the extract',
    'demo.client.signals': 'Observed signals',
    'demo.client.missing': 'Not in the extract',
    'demo.client.missingEmpty': 'Every field the ranking needs is present for this client.',
    'demo.client.nextStep': 'Next review step',
    'demo.client.limits': 'The card describes the uploaded data. It does not assess suitability, does not score the client as a person and does not anticipate what they will do. The adviser decides whether a conversation is appropriate.',
    'demo.client.field.id': 'Client id',
    'demo.client.field.country': 'Country',
    'demo.client.field.age': 'Age',
    'demo.client.field.risk': 'Declared risk profile',
    'demo.client.field.knowledge': 'Knowledge score',
    'demo.client.field.experience': 'Years of experience',
    'demo.client.field.portfolio': 'Portfolio value',
    'demo.client.field.allocation': 'Allocation',
    'demo.client.field.home': 'Home-market weight',
    'demo.client.field.drift': 'Drift vs model',
    'demo.client.field.leverage': 'Leverage',
    'demo.client.field.coverage': 'Evidence coverage',
    'demo.client.field.observed': 'Data observed',
    'demo.client.value.risk': '{value}/10',
    'demo.client.value.knowledge': '{value}/100',
    'demo.client.value.years': '{value} years',
    'demo.client.value.drift': '{value} points',
    'demo.client.value.leverage': '{value}x',
    'demo.client.value.allocation': 'cash {cash}% · funds and ETFs {funds}% · single stocks {stocks}%',
    'demo.client.value.percent': '{value}%',

    'demo.report.title': '3. Review report',
    'demo.report.subtitle': 'The one-page record of the review of {name}, as it would be filed.',
    'demo.report.print': 'Print or save as PDF',
    'demo.report.section.sources': 'Sources and dates',
    'demo.report.section.coverage': 'Coverage',
    'demo.report.section.indicators': 'Observed indicators',
    'demo.report.section.verify': 'To verify before the conversation',
    'demo.report.section.limits': 'What this report does not say',
    'demo.report.sources.body': 'Single source: {file}, uploaded on {imported}. Data observed on {observed}. {rows} rows, {columns} columns.',
    'demo.report.sources.undated': 'Single source: {file}, uploaded on {imported}. This client carried no observation date, so nothing here can be treated as current until that is confirmed.',
    'demo.report.coverage.body': '{value}% of the fields the ranking needs are present for this client. The rest are listed below as missing, and were not estimated.',
    'demo.report.verify.empty': 'No missing field was detected for this client. Confirming the source date with the client record is still good practice.',
    'demo.report.limits.body': 'This report does not claim a result and does not forecast the client\'s behaviour. It does not measure portfolio performance, it does not replace the suitability or appropriateness assessment, and it grants no regulatory approval. It records what the uploaded data showed on the date above, what was missing, and the review step the adviser chose.',
    'demo.report.footer': 'Synthetic example generated by the FinanSee public demo. Not a client document.',

    'demo.cta.title': 'Run this on your own book',
    'demo.cta.body': 'The 30-day trial works on a CSV or XLSX extract of your own clients. The import checks every column before saving anything, and missing fields stay visible as missing.',
    'demo.cta.primary': 'Open the 30-day trial',
    'demo.cta.secondary': 'Download a sample ready for the trial (120 clients)',
    'demo.cta.note': '30 days, no card and no automatic conversion. Two fields to open the account: email and password.',

    'demo.firm.city': 'Madrid',
    'demo.adviser.lead': 'Álvaro Nieto',
    'demo.adviser.analyst': 'Clara Sanchís',

    // Observed signals. Each one is a description of a value in the extract.
    'demo.signal.single_stock_concentration': 'Single-stock concentration: {value}% of the portfolio',
    'demo.signal.home_bias': 'Home-market exposure: {value}%',
    'demo.signal.cash_drag': 'Unallocated cash: {value}%',
    'demo.signal.risk_above_knowledge': 'Declared risk {risk}/10 with a knowledge score of {knowledge}/100',
    'demo.signal.allocation_drift': 'Drift against the reference model: {value} points',
    'demo.signal.leverage': 'Leverage recorded: {value}x',
    'demo.signal.no_rebalance': 'No rebalancing recorded in {value} months',
    'demo.signal.recent_switch': '{value} fund switches in the last 90 days',

    // Fields the extract did not contain.
    'demo.missing.missing_source_date': 'The observation date of the data',
    'demo.missing.missing_knowledge': 'The knowledge score',
    'demo.missing.missing_experience': 'Years of experience',
    'demo.missing.missing_experience_detail': 'What the recorded experience covers',
    'demo.missing.missing_mandate': 'The mandate or reference model to compare against',
    'demo.missing.stale_evidence': 'A recent extract: the most recent data is from {observed}',

    // Why a client is on the review list.
    'demo.reason.low_risk_high_concentration': 'Declared risk is conservative while the portfolio is concentrated',
    'demo.reason.declared_risk_vs_knowledge': 'Declared risk is high and the knowledge score is low',
    'demo.reason.drift_without_date': 'Drift is present and the data carries no date',
    'demo.reason.concentration_with_leverage': 'Concentration and recorded leverage together',
    'demo.reason.cash_without_mandate': 'Large cash position with no mandate to compare it to',
    'demo.reason.stale_evidence_with_drift': 'Drift measured on data that is months old',
    'demo.reason.concentration_single_name': 'Concentration in a single holding',
    'demo.reason.frequent_switching': 'Frequent switches in a short period',
    'demo.reason.home_concentration': 'Most of the portfolio is in the home market',
    'demo.reason.incomplete_profile': 'The profile is incomplete for ranking',

    // The review step, chosen by the adviser and recorded.
    'demo.nextStep.confirm_mandate': 'Confirm the mandate on file, then review the concentration against it.',
    'demo.nextStep.document_rationale': 'Record the rationale for the current positioning before the next instruction.',
    'demo.nextStep.verify_source_then_review': 'Confirm the source date with the client record before treating any signal as current.',
    'demo.nextStep.request_missing_fields': 'Ask the back office for the missing fields and a fresh extract.',
    'demo.nextStep.schedule_review_call': 'Put a review conversation on the calendar and prepare the figures above.',

    // --- Registration ------------------------------------------------------
    'register.meta.title': 'Create your account — FinanSee platform for advisers',
    'register.meta.description': 'Open a FinanSee workspace with an email and a password. The 30-day trial runs on your own CSV or XLSX client extract.',
    'register.badge': 'Two fields',
    'register.title': 'Create your account with an email and a password.',
    'register.subtitle': 'That is all we ask now. Your name and your firm\'s details come later, from inside the workspace, once you have seen what it does.',
    'register.step.one.title': 'Email and password',
    'register.step.one.description': 'Two fields. Nothing about your clients.',
    'register.step.two.title': 'Confirm the email',
    'register.step.two.description': 'A 6-digit code, usually within 30 seconds.',
    'register.step.three.title': 'Look around',
    'register.step.three.description': 'The workspace opens on a synthetic book. Upload your own extract when you want to.',
    'register.form.title': 'Open your workspace',
    'register.form.email': 'Email',
    'register.form.emailHint': 'A work email is best, since it will own the workspace. Any address works.',
    'register.form.emailPlaceholder': 'you@yourfirm.com',
    'register.form.password': 'Password',
    'register.form.passwordPlaceholder': 'At least 10 characters',
    'register.form.passwordHint': 'At least 10 characters. Length matters more than symbols, so a short phrase works well.',
    'register.form.passwordStrength': 'Strength: {label}.',
    'register.form.submit': 'Create account',
    'register.form.submitting': 'Creating the account...',
    'register.form.referral': 'I have a referral code',
    'register.form.referralPlaceholder': 'Partner or referral code',
    'register.form.termsIntro': 'By creating an account you accept our:',
    'register.form.termsLink': 'Terms',
    'register.form.privacyLink': 'Privacy Policy',
    'register.form.haveAccount': 'Already have an account?',
    'register.form.signIn': 'Sign in',
    'register.demoLink': 'Rather look first? Open the public demo — no account needed.',
    'register.error.emailRequired': 'Enter your email',
    'register.error.emailInvalid': 'Enter a valid email address',
    'register.error.passwordRequired': 'Choose a password',
    'register.error.passwordShort': 'Use at least 10 characters',
    'register.error.fixForm': 'Check the form',
    'register.password.weak': 'short',
    'register.password.fair': 'fine',
    'register.password.strong': 'strong',
    'register.toast.success': 'Account created',
    'register.toast.successBody': 'We sent a verification code to your email.',

    // --- Workspace profile, asked after the first sign-in ------------------
    'profile.title': 'Tell us who the workspace belongs to',
    'profile.body': 'We did not ask at registration. These three details name your workspace and appear on the reports you export.',
    'profile.field.name': 'Your name',
    'profile.field.business': 'Firm name',
    'profile.field.employees': 'People who will use it',
    'profile.employees.solo': 'Just me',
    'profile.employees.small': '2 to 4',
    'profile.employees.medium': '5 to 10',
    'profile.employees.large': 'More than 10',
    'profile.submit': 'Save',
    'profile.later': 'Later',
    'profile.saved': 'Saved',
    'profile.error': 'Could not save. Try again.',
    'profile.error.nameRequired': 'Enter your name',
    'profile.error.businessRequired': 'Enter the firm name',
    'otp.badge': 'Email verification',
    'otp.hero.title': 'Verify your admin inbox before the workspace opens.',
    'otp.hero.body': 'We use a 6-digit code to confirm the admin email. Once verified, you can sign in and continue with the plan, the data upload and the workspace setup.',
    'otp.next.title': 'What happens next',
    'otp.next.one': 'Enter the code from your inbox to activate the admin account.',
    'otp.next.two': 'If delivery takes longer than 30 seconds, keep this page open and resend the code.',
    'otp.next.three': 'After verification we return you to login with the same email prefilled.',
    'otp.card.title': 'Verify your email',
    'otp.card.body': 'Enter the 6-digit code that was sent to the inbox you registered with.',
    'otp.alert.sending': 'We are delivering your code. Keep this page open while the email is sent.',
    'otp.alert.waiting': 'The code usually arrives within 30 seconds. Check spam if it has not arrived after 2 minutes.',
    'otp.field.email': 'Email address',
    'otp.field.code': 'Verification code',
    'otp.field.codeHelp': 'Enter the 6-digit code from your email.',
    'otp.cta.verify': 'Verify and continue',
    'otp.cta.verifying': 'Verifying...',
    'otp.cta.resend': 'Resend the code',
    'otp.cta.resending': 'Sending...',
    'otp.cta.back': 'Back to register',
    'otp.wrongEmail': 'Wrong email?',
    'otp.restart': 'Restart registration',
    'otp.toast.emailRequired.title': 'Email required',
    'otp.toast.emailRequired.body': 'Please enter your email address.',
    'otp.toast.verified.title': 'Verification successful',
    'otp.toast.verified.body': 'Email verified.',
    'otp.toast.failed.title': 'Verification failed',
    'otp.toast.failed.body': 'That code is not valid. Please try again.',
    'otp.toast.resent.title': 'Code resent',
    'otp.toast.resent.body': 'A new code was sent to your email.',
    'otp.toast.resendFailed.title': 'Resend failed',
    'otp.toast.resendFailed.body': 'We could not resend the code. Please try again.',
    'login.badge': 'Platform access',
    'login.hero.title': 'Open your FinanSee workspace from one secure sign-in.',
    'login.after.title': 'This is what loads after sign-in',
    'login.after.queue.label': 'Priority queue',
    'login.after.queue.value': 'Who to call today',
    'login.after.queue.meta': 'RM workspace',
    'login.after.evidence.label': 'Evidence review',
    'login.after.evidence.value': 'Missing suitability trail',
    'login.after.evidence.meta': 'Compliance workspace',
    'login.after.drift.label': 'Portfolio drift',
    'login.after.drift.value': 'Risk mismatch surfaced',
    'login.after.drift.meta': 'Portfolio Adviser workspace',
    'login.card.title': 'Sign in',
    'login.card.body': 'Your role workspace opens after authentication.',
    'login.alert.session.title': 'Previous session needs attention',
    'login.alert.session.body': 'Please sign in again to continue.',
    'login.alert.verified.title': 'Email verified',
    'login.alert.verified.body': 'Sign in with the same email and password you used during registration.',
    'login.field.email': 'Email address',
    'login.field.password': 'Password',
    'login.field.passwordPlaceholder': 'Enter your password',
    'login.field.forgot': 'Forgot?',
    'login.field.role': 'Role',
    'login.field.adminEmail': 'Firm admin email',
    'login.employee.toggle': 'I am signing in as an employee role',
    'login.employee.help': 'Use this for RM, Compliance Officer, Portfolio Adviser, or Head of Advisory credentials created by a firm admin.',
    'login.cta.submit': 'Sign in',
    'login.cta.loading': 'Checking access...',
    'login.newHere': 'New here?',
    'login.register': 'Register an admin account',
    'login.role.rm': 'Relationship Manager',
    'login.role.rm.desc': 'Review assigned portfolios, customer insights, risk ranking, and daily priorities.',
    'login.role.portfolio': 'Portfolio Adviser',
    'login.role.portfolio.desc': 'Open advisory-facing customer insights and portfolio benchmark views.',
    'login.role.compliance': 'Compliance Officer',
    'login.role.compliance.desc': 'Inspect audit activity, controls, and compliance-oriented score views.',
    'login.role.head': 'Head of Advisory',
    'login.role.head.desc': 'Access high-level benchmark and advisory leadership dashboards.',
    'login.toast.ok.title': 'Signed in',
    'login.toast.ok.body': 'Opening your workspace.',
    'login.toast.ok.role': 'Opening your {role} workspace.',
    'login.toast.failed.title': 'Sign-in failed',
    'login.toast.failed.body': 'Invalid credentials.',
    'login.toast.error.title': 'Error',
    'login.toast.error.body': 'Something went wrong. Please try again.',
    'login.toast.offline': 'Unable to connect to the server. Please check your connection.',
    'login.hero.body': 'Workspace owners sign in directly. Employee users can add their firm admin context when they need RM, Compliance, Portfolio Adviser, or Head of Advisory access.',
    'login.point.one': 'Role workspace opens after sign-in',
    'login.point.two': 'Admin and RM access for Team',
    'login.point.three': 'Business roles for specialist teams',
};

export default en;
